import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Menu, 
  X, 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin,
  Building2,
  Factory,
  Sparkles,
  Bug,
  Rat,
  Shield,
  TreePine,
  CheckCircle2,
  Send,
  MessageCircle
} from "lucide-react";

// Initialize EmailJS
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "");

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Data
const COMPANY = {
  name: "Pulish5",
  piva: "01263280578",
  address: "Viale Tommaso Moroni 26, Rieti",
  phone: "346 378 5564",
  whatsapp: "393463785564", // WhatsApp number with country code (39 for Italy)
  email: "pulishsrl@gmail.com",
  logo: "https://customer-assets.emergentagent.com/job_90618426-7f60-45c8-ab5a-ac1d673f2289/artifacts/tv14kyma_image.png"
};

// Services Data
const SERVICES = [
  {
    id: "pulizie-civili",
    title: "Pulizie Civili",
    description: "Pulizia professionale per abitazioni, condomini e uffici. Garantiamo ambienti impeccabili con prodotti certificati.",
    icon: Building2,
    span: "md:col-span-8"
  },
  {
    id: "pulizie-industriali",
    title: "Pulizie Industriali",
    description: "Servizi specializzati per capannoni, magazzini e stabilimenti produttivi.",
    icon: Factory,
    span: "md:col-span-4"
  },
  {
    id: "trattamento-pavimenti",
    title: "Trattamento Pavimenti",
    description: "Lucidatura, ceratura e trattamenti specifici per ogni tipo di pavimentazione.",
    icon: Sparkles,
    span: "md:col-span-4"
  },
  {
    id: "disinfestazioni",
    title: "Disinfestazioni",
    description: "Eliminazione di insetti e parassiti con metodi sicuri ed ecologici.",
    icon: Bug,
    span: "md:col-span-4"
  },
  {
    id: "derattizzazioni",
    title: "Derattizzazioni",
    description: "Interventi professionali per la rimozione e prevenzione di roditori.",
    icon: Rat,
    span: "md:col-span-4"
  },
  {
    id: "sanificazioni",
    title: "Sanificazioni",
    description: "Sanificazione certificata di ambienti con tecnologie all'avanguardia per la massima sicurezza.",
    icon: Shield,
    span: "md:col-span-8"
  },
  {
    id: "giardinaggio",
    title: "Giardinaggio",
    description: "Cura e manutenzione di giardini, aree verdi e spazi esterni.",
    icon: TreePine,
    span: "md:col-span-4"
  }
];

// Navigation Links
const NAV_LINKS = [
  { href: "#home", label: "Home" },
  { href: "#chi-siamo", label: "Chi Siamo" },
  { href: "#servizi", label: "Servizi" },
  { href: "#contatti", label: "Contatti" }
];

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 bg-white border-b border-slate-200 ${
        isScrolled ? "shadow-md" : ""
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#home" 
            onClick={(e) => scrollToSection(e, "#home")}
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img 
              src={COMPANY.logo} 
              alt={COMPANY.name} 
              className="h-12 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-slate-600 hover:text-blue-600 font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(" ", "-")}-link`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button 
              onClick={(e) => scrollToSection(e, "#contatti")}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 btn-shine"
              data-testid="header-cta-button"
            >
              Richiedi Preventivo
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 mobile-menu-enter" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-slate-600 hover:text-blue-600 font-medium py-2"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(" ", "-")}-link`}
                >
                  {link.label}
                </a>
              ))}
              <Button 
                onClick={(e) => scrollToSection(e, "#contatti")}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg w-full mt-2"
                data-testid="mobile-cta-button"
              >
                Richiedi Preventivo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContacts = (e) => {
    e.preventDefault();
    document.querySelector("#contatti")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section 
      id="home" 
      className="min-h-screen hero-gradient flex items-center pt-20"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className="animate-fadeInUp">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-4 block">
              Servizi di Pulizia Professionale
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold text-slate-900 mb-6 leading-tight hero-title">
              L'eccellenza del{" "}
              <span className="text-blue-600">Pulito Professionale</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-500 leading-relaxed mb-8 max-w-xl">
              Servizi di pulizia e sanificazione per aziende e privati a Rieti. 
              Affidabilità, competenza e risultati garantiti da oltre 10 anni di esperienza.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={scrollToContacts}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-8 py-6 text-lg btn-shine"
                data-testid="hero-cta-button"
              >
                Richiedi un Preventivo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#servizi")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="border-slate-300 hover:bg-slate-50 rounded-lg px-8 py-6 text-lg"
                data-testid="hero-services-button"
              >
                Scopri i Servizi
              </Button>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 mt-10 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-sm text-slate-600">Preventivi Gratuiti</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-sm text-slate-600">Prodotti Certificati</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-sm text-slate-600">Personale Qualificato</span>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="animate-fadeInUp stagger-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-blue-100/50 rounded-3xl blur-2xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1771678040857-51d00a11be7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBlbXB0eSUyMG9mZmljZSUyMGJyaWdodHxlbnwwfHx8fDE3NzUyOTM4Mzh8MA&ixlib=rb-4.1.0&q=85"
                alt="Ufficio moderno pulito"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                data-testid="hero-image"
              />
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 animate-fadeIn stagger-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">100%</p>
                    <p className="text-sm text-slate-500">Soddisfazione</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Chi Siamo Section
const ChiSiamoSection = () => {
  return (
    <section 
      id="chi-siamo" 
      className="py-24 lg:py-32 bg-white"
      data-testid="chi-siamo-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative animate-fadeInUp order-2 lg:order-1">
            <div className="absolute -inset-4 bg-blue-50 rounded-3xl"></div>
            <img 
              src="https://images.unsplash.com/photo-1603712725038-e9334ae8f39f?crop=entropy&cs=srgb&fm=jpg"
              alt="Team di pulizia professionale"
              className="relative rounded-2xl shadow-xl w-full h-auto object-cover"
              data-testid="about-image"
            />
          </div>

          {/* Content */}
          <div className="animate-fadeInUp stagger-2 order-1 lg:order-2">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-4 block">
              Chi Siamo
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-slate-900 mb-6">
              Professionisti del pulito dal cuore della Sabina
            </h2>
            <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-6">
              <strong className="text-slate-700">Pulish5</strong> è un'azienda con sede a Rieti, 
              specializzata in servizi di pulizia professionale per privati e aziende. 
              La nostra missione è garantire ambienti sani, puliti e accoglienti attraverso 
              l'utilizzo di tecniche innovative e prodotti certificati.
            </p>
            <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8">
              Con un team qualificato e attrezzature all'avanguardia, offriamo soluzioni 
              personalizzate per ogni esigenza: dalle pulizie domestiche alla sanificazione 
              industriale, dalla disinfestazione al giardinaggio.
            </p>
            
            {/* Contact Info Cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Sede Operativa</p>
                  <p className="font-semibold text-slate-900">{COMPANY.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telefono</p>
                  <a 
                    href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                    className="font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                    data-testid="about-phone-link"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServiziSection = () => {
  return (
    <section 
      id="servizi" 
      className="py-24 lg:py-32 bg-slate-50"
      data-testid="servizi-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-4 block">
            I Nostri Servizi
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-slate-900 mb-6">
            Soluzioni complete per ogni esigenza
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
            Offriamo una gamma completa di servizi di pulizia e manutenzione, 
            sempre con la massima professionalità e attenzione ai dettagli.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SERVICES.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className={`${service.span} col-span-1 bg-white rounded-xl border border-slate-200 p-8 service-card animate-fadeInUp stagger-${index + 1}`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="h-14 w-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                  <Icon className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-500 leading-relaxed">
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContattiSection = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    telefono: "",
    servizio: "",
    messaggio: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, servizio: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to backend database
      const response = await axios.post(`${API}/contact`, formData);
      
      // Send email notification via EmailJS
      const emailServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const emailTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      
      if (emailServiceId && emailTemplateId) {
        try {
          await emailjs.send(emailServiceId, emailTemplateId, {
            from_name: formData.nome,
            from_email: formData.email,
            phone: formData.telefono || "Non fornito",
            service: formData.servizio || "Non specificato",
            message: formData.messaggio,
            to_email: COMPANY.email
          });
        } catch (emailError) {
          console.warn("EmailJS notification failed:", emailError);
          // Continue anyway - the form data is saved in the database
        }
      }
      
      if (response.data) {
        toast.success("Messaggio inviato con successo!", {
          description: "Ti ricontatteremo al più presto."
        });
        setFormData({
          nome: "",
          email: "",
          telefono: "",
          servizio: "",
          messaggio: ""
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Errore nell'invio", {
        description: "Si prega di riprovare più tardi."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contatti" 
      className="py-24 lg:py-32 bg-slate-950 text-white"
      data-testid="contatti-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div className="animate-fadeInUp">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-blue-400 mb-4 block">
              Contattaci
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white mb-6">
              Richiedi un preventivo gratuito
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-10">
              Compila il modulo e ti risponderemo entro 24 ore con un preventivo 
              personalizzato e senza impegno.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <a 
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                data-testid="contact-phone-link"
              >
                <div className="h-12 w-12 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telefono</p>
                  <p className="font-semibold text-white">{COMPANY.phone}</p>
                </div>
              </a>
              
              <a 
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                data-testid="contact-email-link"
              >
                <div className="h-12 w-12 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-semibold text-white">{COMPANY.email}</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl">
                <div className="h-12 w-12 bg-blue-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Indirizzo</p>
                  <p className="font-semibold text-white">{COMPANY.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-fadeInUp stagger-2">
            <form 
              onSubmit={handleSubmit}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
              data-testid="contact-form"
            >
              <h3 className="text-2xl font-semibold text-white mb-6">
                Richiedi un Preventivo
              </h3>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Nome e Cognome *
                  </label>
                  <Input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    placeholder="Il tuo nome"
                    className="form-input-dark w-full rounded-lg h-12"
                    data-testid="contact-form-nome"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="email@esempio.com"
                      className="form-input-dark w-full rounded-lg h-12"
                      data-testid="contact-form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Telefono
                    </label>
                    <Input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      placeholder="123 456 7890"
                      className="form-input-dark w-full rounded-lg h-12"
                      data-testid="contact-form-telefono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Servizio di Interesse
                  </label>
                  <Select onValueChange={handleSelectChange} value={formData.servizio}>
                    <SelectTrigger 
                      className="form-input-dark w-full rounded-lg h-12"
                      data-testid="contact-form-servizio"
                    >
                      <SelectValue placeholder="Seleziona un servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map(service => (
                        <SelectItem key={service.id} value={service.title}>
                          {service.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Messaggio *
                  </label>
                  <Textarea
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={handleInputChange}
                    required
                    placeholder="Descrivi le tue esigenze..."
                    rows={4}
                    className="form-input-dark w-full rounded-lg resize-none"
                    data-testid="contact-form-messaggio"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-12 text-base font-semibold btn-shine"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? (
                    "Invio in corso..."
                  ) : (
                    <>
                      Invia Richiesta
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer 
      className="bg-slate-950 text-slate-400 py-16 border-t border-slate-800 relative overflow-hidden"
      data-testid="footer"
    >
      {/* Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="footer-watermark">PULISH5</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <img 
              src={COMPANY.logo} 
              alt={COMPANY.name}
              className="h-14 w-auto mb-6 brightness-0 invert opacity-80"
            />
            <p className="text-slate-500 mb-4">
              Servizi di pulizia professionale per aziende e privati a Rieti.
            </p>
            <p className="text-sm text-slate-600">
              P.IVA: {COMPANY.piva}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Link Rapidi</h4>
            <nav className="space-y-3">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block text-slate-500 hover:text-white transition-colors"
                  data-testid={`footer-${link.label.toLowerCase().replace(" ", "-")}-link`}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contatti</h4>
            <div className="space-y-3 text-slate-500">
              <p>{COMPANY.address}</p>
              <p>
                <a 
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  className="hover:text-white transition-colors"
                  data-testid="footer-phone-link"
                >
                  {COMPANY.phone}
                </a>
              </p>
              <p>
                <a 
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-white transition-colors"
                  data-testid="footer-email-link"
                >
                  {COMPANY.email}
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-slate-800 text-center text-sm text-slate-600">
          <p>
            &copy; {currentYear} {COMPANY.name}. Tutti i diritti riservati.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Widget Component
const WhatsAppWidget = () => {
  const message = encodeURIComponent("Ciao! Vorrei richiedere un preventivo per i vostri servizi di pulizia.");
  const whatsappUrl = `https://wa.me/${COMPANY.whatsapp}?text=${message}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
      data-testid="whatsapp-widget"
      aria-label="Contattaci su WhatsApp"
    >
      <svg 
        viewBox="0 0 24 24" 
        className="h-6 w-6 fill-current"
        aria-hidden="true"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="font-semibold hidden sm:inline">Scrivici su WhatsApp</span>
    </a>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen" data-testid="app-container">
      <Toaster richColors position="top-center" />
      <Header />
      <main>
        <HeroSection />
        <ChiSiamoSection />
        <ServiziSection />
        <ContattiSection />
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

export default App;
